import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, Share2, Check, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import PostCard, { PostCardSkeleton } from './PostCard.jsx';
import { imageUrl, formatDate, PATHS } from '../../lib/opinly';
import { CONTACT } from '../../mock';

const NAVY = '#1a3a5c';
const TEAL = '#3d94b8';

export function AuthorAvatar({ author, size = 'h-8 w-8' }) {
  const src = imageUrl(author?.fileKey);
  if (src) return <img src={src} alt="" className={`${size} rounded-full object-cover`} />;
  return <span className={`${size} rounded-full flex items-center justify-center`} style={{ background: '#e6f2f7' }}><User className="h-1/2 w-1/2" style={{ color: TEAL }} /></span>;
}

export function PostHeader({ post, minutes, onShare, copied }) {
  return (
    <header className="hero-bg pt-10 pb-12 md:pt-14 md:pb-16">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <nav aria-label="Breadcrumb" className="text-sm text-slate-500 flex flex-wrap gap-1 items-center">
          <Link to="/" className="hover:underline" style={{ color: '#2d7d9a' }}>Startseite</Link><span>/</span>
          <Link to={PATHS.index} className="hover:underline" style={{ color: '#2d7d9a' }}>Blog</Link>
          {post.category && <><span>/</span><Link to={PATHS.category(post.category.slug)} className="hover:underline" style={{ color: '#2d7d9a' }}>{post.category.name}</Link></>}
        </nav>
        <h1 data-testid="post-title" className="mt-5 text-3xl md:text-5xl font-extrabold tracking-tight leading-tight" style={{ color: NAVY }}>{post.title}</h1>
        {post.description && <p className="mt-5 text-lg text-slate-600">{post.description}</p>}
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
          {post.author && (
            <Link to={PATHS.author(post.author.slug)} className="inline-flex items-center gap-2 hover:underline">
              <AuthorAvatar author={post.author} />
              <span className="font-semibold" style={{ color: NAVY }}>{post.author.name}</span>
            </Link>
          )}
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> <time dateTime={post.firstPublishedAt}>{formatDate(post.firstPublishedAt)}</time></span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {minutes} Min. Lesezeit</span>
          <button data-testid="post-share" onClick={onShare} className="inline-flex items-center gap-1.5 font-semibold hover:underline" style={{ color: '#2d7d9a' }}>
            {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />} {copied ? 'Link kopiert' : 'Teilen'}
          </button>
        </div>
      </div>
    </header>
  );
}

export function PostHeroImage({ titleFile, fallbackAlt }) {
  const hero = imageUrl(titleFile?.fileKey);
  if (!hero) return null;
  return (
    <figure className="max-w-5xl mx-auto px-5 lg:px-8 -mt-6 md:-mt-8">
      <img data-testid="post-hero-image" src={hero} alt={titleFile?.altText || fallbackAlt} className="w-full aspect-[16/9] object-cover rounded-3xl shadow-xl" />
    </figure>
  );
}

export function PostFaqs({ faqs }) {
  if (!faqs?.length) return null;
  return (
    <section className="mt-14" data-testid="post-faqs">
      <h2 className="text-2xl font-extrabold mb-5" style={{ color: NAVY }}>Häufige Fragen</h2>
      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map((f) => (
          <AccordionItem key={f.question} value={f.question} className="card-soft border-0 px-6 py-1">
            <AccordionTrigger className="text-left font-semibold hover:no-underline py-4" style={{ color: NAVY }}>{f.question}</AccordionTrigger>
            <AccordionContent className="text-slate-600 leading-relaxed pb-4">{f.answer}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export function PostTags({ tags }) {
  if (!tags?.length) return null;
  return (
    <div className="mt-10 flex flex-wrap gap-2" data-testid="post-tags">
      {tags.map((t) => <Link key={t.slug} to={PATHS.tag(t.slug)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-600 hover:border-teal-300">#{t.name}</Link>)}
    </div>
  );
}

export function PostCta() {
  return (
    <div className="mt-14 rounded-3xl p-8 md:p-10 text-white" style={{ background: 'linear-gradient(135deg, #3d94b8 0%, #2d7d9a 100%)' }}>
      <h2 className="text-2xl md:text-3xl font-extrabold">Bereit, besser zu lernen?</h2>
      <p className="mt-2 text-white/90">Kostenlose Bedarfsanalyse – wir finden den passenden Tutor für dich.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/buchen"><Button className="bg-white hover:bg-slate-50 rounded-full h-12 px-6 font-semibold" style={{ color: '#2d7d9a' }}>Jetzt Termin buchen</Button></Link>
        <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer"><Button className="btn-whatsapp rounded-full h-12 px-6"><MessageCircle className="mr-2 h-4 w-4" /> WhatsApp</Button></a>
      </div>
    </div>
  );
}

export function PostSidebar({ headings, author }) {
  return (
    <aside className="hidden lg:block">
      <div className="sticky top-28 space-y-8">
        {headings.length > 1 && (
          <nav aria-label="Inhalt" data-testid="post-toc" className="card-soft p-6">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: TEAL }}>Inhalt</p>
            <ul className="mt-3 space-y-2 text-sm">
              {headings.map((h) => <li key={h.id} className={h.level === 3 ? 'pl-3' : ''}><a href={`#${h.id}`} className="text-slate-600 hover:text-slate-900 hover:underline">{h.text}</a></li>)}
            </ul>
          </nav>
        )}
        {author && (
          <div className="card-soft p-6">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: TEAL }}>Autor</p>
            <Link to={PATHS.author(author.slug)} className="mt-3 flex items-center gap-3 hover:underline">
              <AuthorAvatar author={author} size="h-12 w-12" />
              <span className="font-bold" style={{ color: NAVY }}>{author.name}</span>
            </Link>
            {author.bio && <p className="mt-3 text-sm text-slate-600">{author.bio}</p>}
          </div>
        )}
      </div>
    </aside>
  );
}

const SKELETON_KEYS = ['a', 'b', 'c'];

export function RelatedPosts({ posts }) {
  return (
    <section className="py-14 md:py-20" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f5f9fb 100%)' }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h2 className="text-2xl md:text-3xl font-extrabold" style={{ color: NAVY }}>Weitere Beiträge</h2>
          <Link to={PATHS.index} className="text-sm font-semibold hover:underline" style={{ color: '#2d7d9a' }}>Alle Beiträge</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="post-related">
          {posts.length ? posts.map((p) => <PostCard key={p.slug} post={p} />) : SKELETON_KEYS.map((k) => <PostCardSkeleton key={k} />)}
        </div>
      </div>
    </section>
  );
}
